import styles from "./dropIndicator.module.scss";
import type { DropAxis } from "./types";
import type { RowIndicator } from "./useDropTarget";

/** The compiled `dropIndicator.module.scss` class map — re-exported so a consumer that needs a
 *  class this file doesn't already resolve for it (e.g. `.dropInside` for a single-box "inside"
 *  frame outside the above/below/inside vocabulary `rowIndicatorClass` covers) can still reference
 *  the one shared stylesheet instead of hand-rolling an equivalent rule. */
export const dropIndicatorStyles = styles;

/**
 * The one shared mapping from a `useDropTarget({ indicator: "rowClass" })` `rowIndicator` to the
 * CSS class that draws it directly on `item`'s own row element — for a Vue-owned (non-virtualized)
 * list that can bind `:class` straight onto its `v-for` row, e.g. `EditableTab.vue`,
 * `SCAInputTags.vue`. A virtualized/DOM-external consumer (ag-Grid rows) instead uses
 * `applyElementIndicator`/`clearElementIndicators` (`elementIndicator.ts`), which draw from the
 * same stylesheet by a different (imperative) route.
 *
 * Returns undefined when `item` isn't the currently indicated row — bind directly into `:class`,
 * e.g. `:class="[..., rowIndicatorClass(dropTarget.rowIndicator.value, item, 'horizontal')]"`.
 */
export function rowIndicatorClass<TItem>(
  indicator: RowIndicator<TItem> | null,
  item: TItem,
  axis: DropAxis = "vertical",
): string | undefined {
  if (!indicator || indicator.item !== item) return undefined;
  if (indicator.side === "inside") return styles.dropInside;
  if (axis === "horizontal") {
    return indicator.side === "below" ? styles.indicatorAfter : styles.indicatorBefore;
  }
  return indicator.side === "below" ? styles.indicatorBelow : styles.indicatorAbove;
}
